'use client'
import { DevelopersCarousel } from '@/payload-types'
import { cn } from '@/utilities/ui'
import Slider from 'react-slick'
import 'slick-carousel/slick/slick.css'
import 'slick-carousel/slick/slick-theme.css'
import Image from 'next/image'
import { ChevronLeft, ChevronRight } from 'lucide-react'
import { useEffect, useMemo, useState } from 'react'
import Link from 'next/link'

type Language = 'en' | 'fr' | 'ar'
type Props = {
  className?: string
  dictionary: any
  lang?: Language
  slides: any[]
} & DevelopersCarousel

function useIsMobile(breakpoint = 640) {
  const [isMobile, setIsMobile] = useState(false)

  useEffect(() => {
    const check = () => setIsMobile(window.innerWidth < breakpoint)
    check()
    window.addEventListener('resize', check)
    return () => window.removeEventListener('resize', check)
  }, [breakpoint])

  return isMobile
}

const NextArrow = ({ onClick }: { onClick?: () => void }) => {
  const isMobile = useIsMobile()
  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        onClick?.(e)
      }}
      className="absolute top-1/2 md:right-[13rem] right-2 -translate-y-1/2 z-20 bg-[#6B6A6A] hover:bg-[#6B6A6A]/95 p-3 rounded-full shadow cursor-pointer"
    >
      <ChevronRight size={isMobile ? 15 : 30} color="white" />
    </div>
  )
}

const PrevArrow = ({ onClick }: { onClick?: () => void }) => {
  const isMobile = useIsMobile()
  return (
    <div
      onClick={(e) => {
        e.stopPropagation()
        e.preventDefault()
        onClick?.(e)
      }}
      className="absolute top-1/2 md:left-[13rem] left-2 -translate-y-1/2 z-20 bg-[#6B6A6A] hover:bg-[#6B6A6A]/95 p-3 rounded-full shadow cursor-pointer"
    >
      <ChevronLeft size={isMobile ? 15 : 30} color="white" />
    </div>
  )
}

function mod(n: number, m: number) {
  return ((n % m) + m) % m
}

export const DevelopersCarouselBlock = ({
  className,
  dictionary,
  lang = 'en',
  slides = [],
}: Props) => {
  function getSliderSettings(count: number) {
    const base = {
      arrows: count > 1,
      dots: count > 1,
      speed: 500,
      slidesToShow: 1,
      slidesToScroll: 1,
      adaptiveHeight: false,
      autoplay: true,
      autoplaySpeed: 2000,
      infinite: true,
      prevArrow: <PrevArrow />,
      nextArrow: <NextArrow />,
    }

    // 1 slide: no center mode, no infinite, just centered banner
    if (count <= 1) {
      return {
        ...base,
        centerMode: false,
      }
    }

    // 2 slides: keep it centered, allow peeks; infinite can feel weird if you dislike duplicates
    if (count === 2) {
      return {
        ...base,
        centerMode: true,
        centerPadding: '220px',
        focusOnSelect: true,
        responsive: [
          { breakpoint: 1024, settings: { centerPadding: '140px' } },
          { breakpoint: 640, settings: { centerPadding: '24px' } },
        ],
      }
    }

    // 3+ slides: best experience with infinite + center peeks
    return {
      ...base,
      centerMode: true,
      centerPadding: '220px',
      focusOnSelect: true,
      responsive: [
        { breakpoint: 1024, settings: { centerPadding: '140px' } },
        { breakpoint: 640, settings: { centerPadding: '24px' } },
      ],
    }
  }

  const [active, setActive] = useState(0)
  const sliderSettings = useMemo(() => {
    const settings = getSliderSettings(slides.length)

    return {
      ...settings,
      // react-slick gives you indices including clones in infinite mode.
      // Normalize them back into [0..slides.length-1]
      beforeChange: (_current: number, next: number) => {
        setActive(mod(next, slides.length))
      },
    }
  }, [slides.length])

  if (slides.length === 0) return <></>

  return (
    <Link
      href={slides[active].link ?? '/developers'}
      target="_blank"
      className={cn('pt-5 bg-light', className)}
    >
      <div className="container mx-auto max-sm:px-4">
        <div className="mx-auto max-w-7xl">
          <Slider
            className={cn('dev-carousel', slides.length === 1 && 'single-slide')}
            {...sliderSettings}
          >
            {slides.map((slide, index) => {
              const isActive = index === active
              return (
                <div key={index} className="px-3">
                  <div className="dev-slide relative mx-auto overflow-hidden rounded-2xl">
                    <Image
                      src={slide.image?.url}
                      alt={slide.alt ?? slide.image?.alt ?? 'carousel image'}
                      fill
                      sizes="(max-width: 640px) 92vw, (max-width: 1024px) 900px, 1000px"
                      className="object-cover"
                      priority={isActive}
                      loading={isActive ? 'eager' : 'lazy'}
                      quality={80}
                    />
                  </div>
                </div>
              )
            })}
          </Slider>
        </div>
        {/* Global CSS to get the exact “center big + side small” look */}
        <style jsx global>{`
          /* FIXED SLIDE SIZE (tweak these to match your design) */
          .dev-carousel .dev-slide {
            width: 100%;
            max-width: 980px; /* center banner width */
            height: 210px; /* banner height */
          }

          @media (max-width: 1024px) {
            .dev-carousel .dev-slide {
              max-width: 900px;
              height: 190px;
            }
          }

          @media (max-width: 640px) {
            .dev-carousel .dev-slide {
              max-width: 100%;
              height: 160px;
            }
          }

          /* Center slide BIG; side slides SMALL (the “peek” effect) */
          .dev-carousel .slick-slide {
            opacity: 0.55;
            transform: scale(0.9);
            transition:
              transform 250ms ease,
              opacity 250ms ease;
          }

          .dev-carousel .slick-center {
            opacity: 1;
            transform: scale(1);
          }

          /* Make arrows sit nicely on top like your screenshot */
          .dev-carousel .slick-prev,
          .dev-carousel .slick-next {
            z-index: 10;
            width: 42px;
            height: 42px;
          }

          .dev-carousel .slick-prev {
            left: 12px;
          }
          .dev-carousel .slick-next {
            right: 12px;
          }

          /* Optional: reduce default slick arrow visual size */
          .dev-carousel .slick-prev:before,
          .dev-carousel .slick-next:before {
            font-size: 32px;
          }

          /* Single slide should NOT be dimmed or scaled */
          .dev-carousel.single-slide .slick-slide {
            opacity: 1 !important;
            transform: none !important;
          }

          .dev-carousel.single-slide .slick-track {
            display: flex;
            justify-content: center;
          }
        `}</style>
      </div>
    </Link>
  )
}
